import { useStreamStore } from '#pplx/frontend/stream/stream.js';
import { useMemo } from 'react';
const HIDDEN_ACTIVE_STREAM_QUERY_SOURCES = new Set([
    'automation_run',
    'computer_side_chat',
    'teammate_worker',
    'teammate_orchestrator',
]);
function isHiddenActiveStreamEntry(entry) {
    return HIDDEN_ACTIVE_STREAM_QUERY_SOURCES.has(entry.query_source ?? '');
}
// Incognito threads never reach history, so their in-flight rows must not
// surface in any list either. The thread stamp wins; the request flag covers
// the placeholder window before the first frame.
function isIncognitoActiveStream(stream, entry) {
    return entry?.privacy_state === 'INCOGNITO' || stream.params?.is_incognito === true;
}
export function getActivelyStreamingThreadsMissionControl({ activeStreams, streams, threads, entries, transientThreads = {}, }) {
    return Array.from(activeStreams)
        .reverse()
        .map((streamId) => {
        const stream = streams[streamId];
        if (!stream) {
            return undefined;
        }
        const threadId = stream.threadId;
        if (threadId && threadId in transientThreads) {
            return undefined;
        }
        const thread = threads.find((thread) => thread.id === threadId);
        const firstEntryId = thread?.entryIds?.[0];
        if (firstEntryId && threadId) {
            const firstEntry = entries[firstEntryId];
            if (!firstEntry) {
                return undefined;
            }
            if (isHiddenActiveStreamEntry(firstEntry) || isIncognitoActiveStream(stream, firstEntry)) {
                return undefined;
            }
            return {
                id: threadId,
                slug: firstEntry.thread_url_slug,
                title: firstEntry.thread_title ??
                    firstEntry.query_str ??
                    stream.placeholderChunk?.query_str ??
                    '',
                rwToken: firstEntry.read_write_token,
                lastUpdated: firstEntry.updated_datetime,
                contextUUID: firstEntry.context_uuid,
                collectionInfo: firstEntry.collection_info,
                displayModel: firstEntry.display_model,
                entryUUID: firstEntry.uuid,
                socialInfo: firstEntry.social_info,
                focus: firstEntry.search_focus,
                mode: firstEntry.mode,
                source: firstEntry.source,
                streamCreatedAt: firstEntry.stream_created_at,
                frontendContextUUID: firstEntry.frontend_context_uuid,
            };
        }
        else {
            // Before the first server frame only the request knows the source.
            if (isIncognitoActiveStream(stream, undefined) ||
                HIDDEN_ACTIVE_STREAM_QUERY_SOURCES.has(stream.params?.query_source ?? stream.placeholderChunk?.query_source ?? '')) {
                return undefined;
            }
            // Placeholder
            return {
                id: '',
                slug: undefined,
                title: stream.placeholderChunk?.query_str ?? '',
                rwToken: null,
                lastUpdated: null,
                contextUUID: stream.placeholderChunk?.context_uuid || null,
                collectionInfo: stream.placeholderChunk?.collection_info ?? null,
                displayModel: null,
                entryUUID: null,
                socialInfo: null,
                focus: null,
                mode: stream.params?.mode?.toUpperCase(),
                source: null,
                streamCreatedAt: new Date().toISOString(),
                frontendContextUUID: stream.placeholderChunk?.frontend_context_uuid,
            };
        }
    })
        .filter((thread) => thread != null);
}
export function _getActivelyStreamingThreads({ activeStreams, streams, threads, entries, transientThreads = {}, }, { includeReconnects = true } = {}) {
    return Array.from(activeStreams)
        .reverse()
        .map((streamId) => {
        const stream = streams[streamId];
        if (!stream || (!includeReconnects && stream.isReconnect)) {
            return undefined;
        }
        const threadId = stream.threadId;
        if (!threadId) {
            return undefined;
        }
        if (threadId in transientThreads) {
            return undefined;
        }
        const thread = threads.find((thread) => thread.id === threadId);
        const firstEntryId = thread?.entryIds?.[0];
        if (!firstEntryId) {
            return undefined;
        }
        const firstEntry = entries[firstEntryId];
        if (!firstEntry) {
            return undefined;
        }
        if (isHiddenActiveStreamEntry(firstEntry) || isIncognitoActiveStream(stream, firstEntry)) {
            return undefined;
        }
        return {
            id: threadId,
            slug: firstEntry.thread_url_slug,
            title: firstEntry.thread_title ??
                firstEntry.query_str ??
                stream.placeholderChunk?.query_str ??
                '',
            rwToken: firstEntry.read_write_token,
            lastUpdated: firstEntry.updated_datetime,
            contextUUID: firstEntry.context_uuid,
            collectionInfo: firstEntry.collection_info,
            displayModel: firstEntry.display_model,
            entryUUID: firstEntry.uuid,
            socialInfo: firstEntry.social_info,
            focus: firstEntry.search_focus,
            mode: firstEntry.mode,
            source: firstEntry.source,
            streamCreatedAt: firstEntry.stream_created_at,
            frontendContextUUID: firstEntry.frontend_context_uuid ?? stream.placeholderChunk?.frontend_context_uuid,
        };
    })
        .filter((thread) => thread != null);
}
const EMPTY_STREAMING_THREADS = [];
export function useGetActivelyStreamingThreads({ includeReconnects = true, } = {}) {
    const store = useStreamStore();
    const activeStreams = store((s) => s.activeStreams);
    const streams = store((s) => s.streams);
    const threads = store((s) => s.threads);
    const entries = store((s) => s.entries);
    const transientThreads = store((s) => s.transientThreads);
    return useMemo(() => {
        if (activeStreams.length === 0)
            return EMPTY_STREAMING_THREADS;
        const streamingThreads = _getActivelyStreamingThreads({
            activeStreams,
            streams,
            threads,
            entries,
            transientThreads,
        }, { includeReconnects });
        // Multiple active entries can temporarily point at the same thread slug/id, so dedupe before rendering.
        const seenSlugs = new Set();
        return streamingThreads.filter((thread) => {
            if (!thread.slug)
                return false;
            if (seenSlugs.has(thread.slug))
                return false;
            seenSlugs.add(thread.slug);
            return true;
        });
    }, [activeStreams, streams, threads, entries, transientThreads, includeReconnects]);
}
//# sourceMappingURL=useGetActivelyStreamingThreads.js.map